import React from "react";
import type { SummaryStats } from "../../types";
import type { ExportTemplateModule } from "../types";
import { formatCurrency, formatNumber } from "../utils";

interface KpiHeroModuleProps {
  summary: SummaryStats;
  module: ExportTemplateModule<"kpi-hero">;
}

const HERO_COLORS = [
  { bg: "linear-gradient(135deg, #ecfdf5, #d1fae5)", accent: "#059669" },
  { bg: "linear-gradient(135deg, #eff6ff, #dbeafe)", accent: "#2563eb" },
  { bg: "linear-gradient(135deg, #fef3c7, #fde68a)", accent: "#d97706" },
  { bg: "linear-gradient(135deg, #fce7f3, #fbcfe8)", accent: "#db2777" },
];

export const KpiHeroModule: React.FC<KpiHeroModuleProps> = ({
  summary,
  module,
}) => {
  const kpis = [
    {
      id: "sales",
      label: "Total Sales",
      value: formatCurrency(summary.total_sales),
      subtitle: `across ${summary.campaign_count} campaigns`,
    },
    {
      id: "spend",
      label: "Media Spend",
      value: formatCurrency(summary.total_spend),
      subtitle: `${formatNumber(summary.total_impressions, { maximumFractionDigits: 0 })} impressions delivered`,
    },
    {
      id: "roas",
      label: "Average ROAS",
      value: `${formatNumber(summary.average_roas, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}x`,
      subtitle: `${formatNumber(summary.total_clicks, { maximumFractionDigits: 0 })} total clicks`,
    },
    {
      id: "units",
      label: "Units Sold",
      value: formatNumber(summary.total_units, { maximumFractionDigits: 0 }),
      subtitle: `${summary.brand_count} brands, ${summary.total_days} days`,
    },
  ];

  return (
    <section className="w-full h-full">
      {module.title && (
        <div className="text-slate-400 uppercase tracking-[0.2em] text-xs font-semibold mb-4">
          {module.title}
        </div>
      )}
      <div className="grid grid-cols-2 gap-6 h-full">
        {kpis.map((kpi, index) => (
          <div
            key={kpi.id}
            className="rounded-3xl border border-slate-100 shadow-[0_25px_60px_rgba(15,23,42,0.08)] p-10 flex flex-col justify-center"
            style={{ background: HERO_COLORS[index].bg }}
          >
            <div
              className="text-xs uppercase tracking-[0.3em] font-semibold mb-3"
              style={{ color: HERO_COLORS[index].accent }}
            >
              {kpi.label}
            </div>
            <div className="text-5xl font-bold text-slate-900 mb-2">
              {kpi.value}
            </div>
            <div className="text-base text-slate-500">{kpi.subtitle}</div>
          </div>
        ))}
      </div>
    </section>
  );
};
